import { captureException } from "@sentry/react";
import { useAtomValue } from "jotai";
import { omit } from "lodash-es";
import toast from "react-hot-toast";
import { UnsplashService } from "../../../../../server/api/unsplash";
import { useToken } from "../../../../../server/auth/use-token";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { channelToFormat } from "../../../../../utils/channel-to-format";
import { useChatMomentService } from "../../../../chat-moment/hooks/use-chat-moment-service";
import { useEmailMomentService } from "../../../../email-moment/hooks/use-email-moment-service";
import { SupabaseFollowUpService } from "../../../../follow-up/server/follow-up-service";
import { useMessageBodyCreator } from "../../../../message/editor/utils/create-message-body/create-message-body";
import { editorIsEmpty } from "../../../../message/editor/utils/editor-is-empty";
import { useScheduleService } from "../../../../schedule/hooks/use-schedule-service";
import { useSegmentSaveAllowed } from "../../../../segment/hooks/use-segment-save-allowed";
import { useSegmentService } from "../../../../segment/hooks/use-segment-service";
import { SupabaseMomentService } from "../../../server/supabase-moment-service";
import { unsplashDownloadUrlAtom } from "../../../types/store";
import { useRoundaboutContext } from "../../roundabout/roundabout-context";
import type { SaveButtonResponse } from "../types";
import type { HandleSaveMoment } from "./types";

type UseHandleSaveConfig = {
  reset: (values: HandleSaveMoment) => void;
  validate: () => Promise<boolean>;
  errorHandlers?: {
    segment?: (err: Error) => void;
    design?: (err: Error) => void;
  };
  refreshCoverImage?: (url?: string | null) => void;
};

export function useHandleSave({
  reset,
  validate,
  errorHandlers,
  refreshCoverImage,
}: UseHandleSaveConfig): {
  loading: boolean;
  callback: (vals: HandleSaveMoment) => Promise<SaveButtonResponse>;
} {
  const getToken = useToken();

  const { data: segment_save_allowed } = useSegmentSaveAllowed();

  const saveSegment = useSegmentService();
  const saveChat = useChatMomentService();
  const saveEmail = useEmailMomentService();
  const saveSchedule = useScheduleService();
  const saveFollowUp = useSupabaseCallback(
    async ({ supabase }, follow_up: HandleSaveMoment["follow_up"]) => {
      if (!follow_up) return;
      const { error } = await new SupabaseFollowUpService(supabase).upsert(follow_up);
      if (error) throw error;
    },
    [],
  );

  const { data: createMessageBody, loading } = useMessageBodyCreator();
  const unplashDownloadUrl = useAtomValue(unsplashDownloadUrlAtom);
  const { captureMomentFormSave } = useRoundaboutContext();

  return {
    loading,
    callback: useSupabaseCallback(
      async ({ supabase, account_id }, vals: HandleSaveMoment): Promise<SaveButtonResponse> => {
        if (loading) return { status: "error" };
        if (!account_id) {
          toast.error("Account not found");
          return { status: "error" };
        }

        if (!createMessageBody) {
          toast.error("No message body creator");
          return { status: "error" };
        }

        try {
          if (segment_save_allowed) await saveSegment(vals.segment ?? null);
        } catch (err) {
          errorHandlers?.segment?.(err as Error);
          return { status: "error" };
        }

        try {
          await saveSchedule(vals.schedule);
          if (!vals.use_lexical_editor) {
            await saveEmail(vals.email_moment ?? null);
            await saveChat(vals.chat_moment ?? null);
          }
          await saveFollowUp(vals.follow_up ?? null);
        } catch (err) {
          captureException(err);
          return { status: "error" };
        }
        if (vals.program_id === undefined) return { status: "error" };
        const { data: messageBodyData, errorMessage } = await createMessageBody({
          editorState: editorIsEmpty(vals.design) ? null : vals.design,
          channel: vals.channel,
          format: channelToFormat(vals.channel ?? "work_email"),
          account_id,
          email_design_id: vals.email_design_id ?? undefined,
          poll_ids: vals.poll_ids ?? undefined,
        });
        if (errorMessage) {
          toast.error("Something went wrong. Please try again.");
          return { status: "error" };
        }

        const valid_on_save = await validate();
        const { error, data } = await new SupabaseMomentService(supabase).upsert({
          ...omit(
            vals,
            "sent",
            "running",
            "composeMethods",
            "status",
            "schedule",
            "segment",
            "email_moment",
            "chat_moment",
            "text_moment",
            "used_merge_fields",
            "unrecoverable_error",
            "static_cc",
            "static_bcc",
            "dynamic_cc",
            "dynamic_bcc",
            "extracted_survey_ids",
            "follow_up",
          ),
          program_id: vals.program_id,
          schedule_id: vals.schedule?.id ?? null,
          segment_id: vals.segment?.id ?? null,
          email_moment_id: vals.email_moment?.id ?? null,
          chat_moment_id: vals.chat_moment?.id ?? null,
          follow_up_id: vals.follow_up?.id ?? null,
          body: messageBodyData?.body ?? null,
          body_text: messageBodyData?.text ?? null,
          valid_on_save,
        });

        const valid = !(error || !data);

        if (!valid) toast.error("Something went wrong. Please try again.");

        if (refreshCoverImage?.(vals.cover_image_path) && unplashDownloadUrl) {
          void new UnsplashService(await getToken())
            .ackDownload(unplashDownloadUrl)
            .catch(console.error);
        }

        reset(vals);

        const validationError = validateMomentContent(
          messageBodyData?.body ?? undefined,
          vals.channel ?? undefined,
        );
        if (validationError) {
          errorHandlers?.design?.(new Error(validationError.error));
          return { status: "invalid", message: validationError.error };
        }
        if (valid) {
          captureMomentFormSave();
        }
        return { status: valid ? "valid" : "error" };
      },
      [
        loading,
        createMessageBody,
        validate,
        refreshCoverImage,
        unplashDownloadUrl,
        reset,
        segment_save_allowed,
        saveSegment,
        saveSchedule,
        saveEmail,
        saveChat,
        getToken,
        captureMomentFormSave,
        errorHandlers,
        saveFollowUp,
      ],
    ),
  };
}

const validateMomentContent = (body?: string, channel?: string): { error: string } | undefined => {
  const twilioMaxMessageLength = 1400;
  if (channel === "twilio" && body && body.length > twilioMaxMessageLength) {
    return {
      error: `Message is too long: message limit (${body.length}/${twilioMaxMessageLength})`,
    };
  }

  if (!body || channel !== "slack") return;
  const { blocks } = JSON.parse(body) as {
    blocks: { type: string; text?: string }[];
  };

  const slackMaxBlocks = 50;
  if (blocks.length > slackMaxBlocks) {
    return {
      error: `Message is too long: over ${slackMaxBlocks} paragraph limit (${blocks.length}/${slackMaxBlocks})`,
    };
  }
  const slackMaxBlockLength = 2500;
  if (blocks.some(({ text }) => text && text.length > slackMaxBlockLength)) {
    return { error: `Paragraph is too long; paragraph limit (${slackMaxBlockLength})` };
  }
};
